import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { Button, Card, Col, Grid, Row, Skeleton, Space, theme } from "antd";
import PageTransition from "@/components/ui/PageTransition";
import { ErrorAlert } from "@/components/shared/ErrorAlert";
import { createModuleRouteGuard } from "@/modules/routeGuards";
import { useTranslation } from "react-i18next";
import { MODULE_BASE_ROUTE } from "../../../index";
import { useCvManagerApplicationQuery } from "../../../api";
import { ApplicationDecisionActions } from "../../../components/applications/ApplicationDecisionActions";
import { ApplicationActionLogsPanel } from "../../../components/applications/ApplicationActionLogsPanel";
import { ApplicationTasksQueuePanel } from "../../../components/applications/ApplicationTasksQueuePanel";
import { ApplicationFormDataSection } from "../../../components/applications/ApplicationFormDataSection";
import { ApplicationJobOfferAssignModal } from "../../../components/applications/ApplicationJobOfferAssignModal";
import { ApplicationEnrichJsonModal } from "../../../components/applications/ApplicationEnrichJsonModal";
import { ApplicationCandidatePersonalDataModal } from "../../../components/applications/ApplicationCandidatePersonalDataModal";
import { ApplicationUserScoreModal } from "../../../components/applications/ApplicationUserScoreModal";
import { ApplicationOrganizationAssignModal } from "../../../components/applications/ApplicationOrganizationAssignModal";
import { buildJobOfferAssignContextFromApplication } from "../../../components/applications/applicationAttachmentUtils";
import { useApplicationJobOfferAssignment } from "../../../components/applications/useApplicationJobOfferAssignment";
import { useApplicationCandidatePersonalDataWorkflow } from "../../../components/applications/useApplicationCandidatePersonalDataWorkflow";
import { useApplicationEnrichJsonWorkflow } from "../../../components/applications/useApplicationEnrichJsonWorkflow";
import { useApplicationOrganizationAssignWorkflow } from "../../../components/applications/useApplicationOrganizationAssignWorkflow";
import { useApplicationUserScoreWorkflow } from "../../../components/applications/useApplicationUserScoreWorkflow";
import { ApplicationSummaryHeader } from "../../../components/applications/summary/ApplicationSummaryHeader";
import { ApplicationSummaryKpiRow } from "../../../components/applications/summary/ApplicationSummaryKpiRow";
import { ApplicationAiSummaryCard } from "../../../components/applications/summary/ApplicationAiSummaryCard";
import { ApplicationScoringCard } from "../../../components/applications/summary/ApplicationScoringCard";
import { ApplicationCategoryTiles } from "../../../components/applications/summary/ApplicationCategoryTiles";
import { ApplicationCandidateCard } from "../../../components/applications/summary/ApplicationCandidateCard";
import { ApplicationMetadataCard } from "../../../components/applications/summary/ApplicationMetadataCard";
import { ApplicationCandidateNotesCard } from "../../../components/applications/summary/ApplicationCandidateNotesCard";
import { parseEvaluationReport } from "../../../components/applications/summary/evaluationReport";

const { useToken } = theme;
const { useBreakpoint } = Grid;

/** Height of the sticky app header (antd Layout header); sticky cards sit below it. */
const APP_HEADER_HEIGHT = 64;

export const Route = createFileRoute(
  "/manage/modules/cvManager/applications/$applicationId/summary",
)({
  component: CvManagerApplicationSummaryPage,
  beforeLoad: createModuleRouteGuard("cv-manager-module"),
});

/** Loading placeholder shaped like the summary page (KPI row + content card). */
function SummarySkeleton() {
  return (
    <PageTransition animation="fadeInUp">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Skeleton.Button active size="medium" style={{ width: 220 }} />
        <Row gutter={[16, 16]}>
          {[0, 1, 2, 3].map((index) => (
            <Col key={index} xs={24} md={6}>
              <Card>
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Space>
    </PageTransition>
  );
}

function CvManagerApplicationSummaryPage() {
  const { token } = useToken();
  const screens = useBreakpoint();
  // Side-by-side columns (xl) keep the shorter card in view below the sticky app header.
  const stickyColumnStyle: React.CSSProperties | undefined = screens.xl
    ? { position: "sticky", top: APP_HEADER_HEIGHT + token.marginMD }
    : undefined;
  const { t } = useTranslation("cv-manager-module");
  const { applicationId } = useParams({
    from: "/manage/modules/cvManager/applications/$applicationId/summary",
  });
  const numericApplicationId = Number(applicationId);
  const {
    data: application,
    isLoading,
    error,
  } = useCvManagerApplicationQuery(
    Number.isNaN(numericApplicationId) ? null : numericApplicationId,
  );
  const personalData = useApplicationCandidatePersonalDataWorkflow();
  const jobOfferAssign = useApplicationJobOfferAssignment();
  const enrichJson = useApplicationEnrichJsonWorkflow();
  const userScore = useApplicationUserScoreWorkflow(application);
  const organizationAssign =
    useApplicationOrganizationAssignWorkflow(application);

  if (isLoading) {
    return <SummarySkeleton />;
  }

  if (error || !application) {
    return (
      <PageTransition animation="fadeInUp">
        <Card>
          <ErrorAlert
            title={t("summary.errorTitle")}
            error={error}
            fallbackDescription={t("summary.errorDescription")}
            action={
              <Link to={MODULE_BASE_ROUTE}>
                <Button>{t("summary.backToCvManager")}</Button>
              </Link>
            }
          />
        </Card>
      </PageTransition>
    );
  }

  const evaluation = application.latest_evaluation;
  const report = parseEvaluationReport(evaluation?.report);
  const candidate = application.candidate_details;
  const candidateName =
    candidate.full_name || `${candidate.first_name} ${candidate.last_name}`;
  const hasAssignedJobOffer = Boolean(application.job_offer);
  const indeedLink =
    application.source?.trim().toLowerCase() === "indeed"
      ? application.indeed_link?.trim() || undefined
      : undefined;
  const openJobOfferAssign = () =>
    jobOfferAssign.openJobOfferAssign(
      buildJobOfferAssignContextFromApplication(application, candidate),
    );

  return (
    <PageTransition animation="fadeInUp">
      <ApplicationSummaryHeader
        application={application}
        candidateName={candidateName}
        jobOfferLabel={jobOfferAssign.getJobOfferLabel(
          application.job_offer,
          application.job_offer_title,
        )}
        indeedLink={indeedLink}
        onEditJobOffer={openJobOfferAssign}
        isJobOfferAssignSubmitting={jobOfferAssign.isSubmitting}
        actions={
          <ApplicationDecisionActions
            applicationId={application.id}
            status={application.status}
            candidateName={candidateName}
            candidateEmail={candidate.email}
            candidatePhone={candidate.phone}
            jobTitle={application.job_offer_title ?? ""}
            application={application}
            onAssignJobOffer={openJobOfferAssign}
            isJobOfferAssignSubmitting={jobOfferAssign.isSubmitting}
            hasAssignedJobOffer={hasAssignedJobOffer}
            onEnrichJson={() =>
              enrichJson.open({
                applicationId: application.id,
                candidateName,
                candidateEmail: candidate.email,
              })
            }
            isEnrichJsonSubmitting={enrichJson.isSubmitting}
          />
        }
      />

      <ApplicationSummaryKpiRow
        application={application}
        onEditUserScore={userScore.openModal}
      />

      <Row
        gutter={[16, 16]}
        align="stretch"
        style={{ marginBottom: token.marginLG }}
      >
        <Col xs={24} xl={14}>
          <ApplicationAiSummaryCard evaluation={evaluation} report={report} />
        </Col>
        <Col xs={24} xl={10}>
          {/* Side by side with the (usually much longer) AI summary, keep the scores in view while scrolling. */}
          <div style={stickyColumnStyle}>
            <ApplicationScoringCard evaluation={evaluation} report={report} />
          </div>
        </Col>
      </Row>

      <ApplicationCategoryTiles
        report={report}
        style={{ marginBottom: token.marginLG }}
      />

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={12}>
          {/* The metadata column (form data, cover letter) is usually taller; keep the candidate card in view. */}
          <div style={stickyColumnStyle}>
            <ApplicationCandidateCard
              application={application}
              candidateName={candidateName}
              onEditPersonalData={() =>
                personalData.openModal({
                  applicationId: application.id,
                  jobOfferId: application.job_offer,
                  candidate,
                  formData: application.form_data,
                })
              }
            />
          </div>
        </Col>
        <Col xs={24} xl={12}>
          <ApplicationMetadataCard
            application={application}
            indeedLink={indeedLink}
            onAssignOrganization={organizationAssign.openModal}
          />
          <ApplicationFormDataSection
            formData={application.form_data}
            coverLetter={application.cover_letter}
          />
          <ApplicationCandidateNotesCard
            coverLetter={application.cover_letter}
            notes={application.notes}
            style={{ marginTop: token.marginMD }}
          />
        </Col>
      </Row>

      <Space
        orientation="vertical"
        size={token.marginLG}
        style={{ width: "100%", marginTop: token.marginLG }}
      >
        <ApplicationActionLogsPanel applicationId={application.id} />
        <ApplicationTasksQueuePanel applicationId={application.id} />
      </Space>

      {jobOfferAssign.context ? (
        <ApplicationJobOfferAssignModal
          open={jobOfferAssign.modalOpen}
          context={jobOfferAssign.context}
          selectedJobOfferId={jobOfferAssign.selectedJobOfferId}
          jobOfferOptions={jobOfferAssign.jobOfferOptions}
          isLoadingOptions={jobOfferAssign.isLoadingOptions}
          confirmLoading={jobOfferAssign.isSubmitting}
          onSelectedJobOfferIdChange={jobOfferAssign.setSelectedJobOfferId}
          onCancel={jobOfferAssign.closeJobOfferAssignModal}
          onConfirm={jobOfferAssign.confirmJobOfferAssign}
        />
      ) : null}

      <ApplicationEnrichJsonModal
        open={enrichJson.modalOpen}
        confirmLoading={enrichJson.isSubmitting}
        file={enrichJson.file}
        targetApplication={{ candidateName, candidateEmail: candidate.email }}
        onCancel={enrichJson.close}
        onSubmit={enrichJson.submit}
        onFileChange={enrichJson.setFile}
      />

      {personalData.context ? (
        <ApplicationCandidatePersonalDataModal
          open={personalData.modalOpen}
          initialCandidate={personalData.context.candidate}
          initialBirthdate={personalData.context.formData?.birthdate}
          onSubmit={personalData.submitPersonalData}
          onCancel={personalData.closeModal}
          loading={personalData.isSubmitting}
          apiErrors={personalData.apiErrors}
        />
      ) : null}

      <ApplicationOrganizationAssignModal
        open={organizationAssign.modalOpen}
        candidateName={candidateName}
        currentOrganizationName={application.organization_name}
        selectedOrganizationId={organizationAssign.selectedOrganizationId}
        organizationOptions={organizationAssign.organizationOptions}
        isLoadingOptions={organizationAssign.isLoadingOptions}
        confirmLoading={organizationAssign.isSubmitting}
        onSelectedOrganizationIdChange={
          organizationAssign.setSelectedOrganizationId
        }
        onCancel={organizationAssign.closeModal}
        onConfirm={organizationAssign.confirm}
      />

      <ApplicationUserScoreModal
        open={userScore.modalOpen}
        grade={userScore.grade}
        note={userScore.note}
        updatedAt={application.recruiter_evaluation?.updated_at}
        confirmLoading={userScore.isSubmitting}
        error={userScore.error}
        onGradeChange={userScore.setGrade}
        onNoteChange={userScore.setNote}
        onCancel={userScore.closeModal}
        onConfirm={userScore.confirm}
      />
    </PageTransition>
  );
}
